/**
 * Basemap Configuration
 *
 * Defines available basemap providers and their tile source configurations.
 * Supports OpenStreetMap, satellite imagery, and terrain maps.
 */

import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import type { Source } from 'ol/source';

export type BasemapType = 'osm' | 'satellite' | 'terrain';

export interface BasemapConfig {
  id: BasemapType;
  name: string;
  description: string;
  attribution: string;
  createSource: () => Source;
}

/**
 * Available basemap configurations
 */
export const BASEMAP_CONFIGS: Record<BasemapType, BasemapConfig> = {
  osm: {
    id: 'osm',
    name: 'OpenStreetMap',
    description: 'Standard street map with roads, labels, and points of interest',
    attribution: '© OpenStreetMap contributors',
    createSource: () => new OSM(),
  },
  satellite: {
    id: 'satellite',
    name: 'Satellite Imagery',
    description: 'High-resolution satellite and aerial imagery',
    attribution:
      'Tiles © Esri — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    createSource: () =>
      new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        maxZoom: 19,
        attributions:
          'Tiles © Esri — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
      }),
  },
  terrain: {
    id: 'terrain',
    name: 'Terrain Map',
    description: 'Topographic map with elevation contours and terrain features',
    attribution: 'Map data: © OpenStreetMap contributors, SRTM | Map style: © OpenTopoMap',
    createSource: () =>
      new XYZ({
        url: 'https://{a-c}.tile.opentopomap.org/{z}/{x}/{y}.png',
        maxZoom: 17,
        attributions:
          'Map data: © OpenStreetMap contributors, SRTM | Map style: © OpenTopoMap (CC-BY-SA)',
      }),
  },
};

/**
 * Get basemap configuration by type
 */
export const getBasemapConfig = (type: BasemapType): BasemapConfig => {
  return BASEMAP_CONFIGS[type];
};

/**
 * Get all available basemap types
 */
export const getAvailableBasemaps = (): BasemapType[] => {
  return Object.keys(BASEMAP_CONFIGS) as BasemapType[];
};

/**
 * Check if a basemap type is valid
 */
export const isValidBasemapType = (type: string): type is BasemapType => {
  return type in BASEMAP_CONFIGS;
};
